import * as React from "react";
import { useRef, useState } from "react";
import * as Sentry from "@sentry/browser";
import HeaderBar from "./commons/components/HeaderBar";
import UserCardMedium from "./commons/components/user/UserCardMedium";
import UserInfo from "./commons/model/UserInfo";
import sfetch from "./commons/utils/super-fetch";
import StripeSubscriptionStatus, {
  getStripeSubscriptionStatusI18nKey,
} from "./commons/model/pricing/StripeSubscriptionStatus";
import SettingsReactivateAccountForm from "./SettingsReactivateAccountForm";
import SubscriptionPlanFull from "./commons/model/pricing/SubscriptionPlanFull";
import SettingsCannotReactivateAccountForm from "./SettingsCannotReactivateAccountForm";
import ModalConfirm from "./commons/components/modal/ModalConfirm";
import ModalDelete from "./commons/components/modal/ModalDelete";
import I18nAction from "./commons/components/I18nAction";
import "./SettingsAccount.scss";
import Country from "./commons/model/address/Country";
import { createRoot } from "react-dom/client";
import { dateWithOptionalTime } from "./commons/utils/date";

interface Subscription {
  nextPaymentDate?: string;
  status?: StripeSubscriptionStatus;
  reactivationPlan?: SubscriptionPlanFull;
  active: boolean;
  billingPortalUrl: string;
  unsubscribeUrl: string;
  reactivateUrl: string;
  validateEndpoint: string;
}

export default function SettingsAccount({
                                          user,
                                          country,
                                          subscription,
                                          csrf,
                                        }: {
  user: UserInfo;
  country?: Country;
  subscription?: Subscription;
  csrf: string;
}) {
  const [displayUnsubscribeModal, setDisplayUnsubscribeModal] = useState<boolean>(false);
  const [displayReactivationModal, setDisplayReactivationModal] = useState<boolean>(false);

  return (
    <>
      <HeaderBar title={i18n["Page.Settings.Account.Title"]} />
      <div className="content">
        <div className="profile">
          <div className="panel with-shadow">
            <div className="panel-title">{i18n["Page.Settings.Account.Profile.Title"]}</div>
            <UserCardMedium user={user} />
          </div>
        </div>
        {subscription && (
          <SubscriptionPanel
            subscription={subscription}
            setDisplayReactivationModal={setDisplayReactivationModal}
            setDisplayUnsubscribeModal={setDisplayUnsubscribeModal}
            csrf={csrf}
          />
        )}
      </div>
      {displayUnsubscribeModal ? (
        <UnsubscribeModal
          setDisplayUnsubscribeModal={setDisplayUnsubscribeModal}
          unsubscribeUrl={subscription.unsubscribeUrl}
        />
      ) : null}
      {displayReactivationModal ? (
        subscription.reactivationPlan ? (
          <ReactivationModal
            country={country}
            setDisplayReactivationModal={setDisplayReactivationModal}
            subscription={subscription}
          />
        ) : (
          <SettingsCannotReactivateAccountForm cancel={() => setDisplayReactivationModal(false)} />
        )
      ) : null}
    </>
  );
}

function SubscriptionPanel({
                             subscription,
                             setDisplayUnsubscribeModal,
                             setDisplayReactivationModal,
                             csrf,
                           }: {
  subscription: Subscription;
  setDisplayUnsubscribeModal: (boolean: boolean) => any;
  setDisplayReactivationModal: (boolean: boolean) => any;
  csrf: string;
}) {
  const billingPortalFormRef = useRef<HTMLFormElement>();

  const actionButton = subscription.active ? (
    <a
      id="unsubscribe-button"
      href="#"
      className="button-secondary danger small"
      onClick={() => setDisplayUnsubscribeModal(true)}
    >
      {i18n["Page.Settings.Account.Subscription.Unsubscribe.Action"]}
    </a>
  ) : subscription.reactivationPlan ? (
    <a
      id="reactivate-button"
      href="#"
      className="button-secondary small"
      onClick={() => setDisplayReactivationModal(true)}
    >
      {i18n["Page.Settings.Account.Subscription.Reactivate.Action"]}
    </a>
  ) : null;

  return (
    <div className="account-subscriptions">
      <div className="panel with-shadow">
        <div className="panel-title input-header">
          {i18n["Page.Settings.Account.Subscription.Title"]}
          {actionButton}
        </div>
        <div className="input-form">
          <span className="input-name">{i18n["Page.Settings.Account.Subscription.Title"]}</span>
          <div>{i18n["Page.Settings.Account.Subscription.Name"]}</div>
        </div>
        {subscription.nextPaymentDate && (
          <div className="input-form">
            <span className="input-name">
              {i18n["Page.Settings.Account.Subscription.NextPayment"]}
            </span>
            <div>{dateWithOptionalTime(subscription.nextPaymentDate)}</div>
          </div>
        )}
        <div className="input-form">
          <span className="input-name">{i18n["Page.Settings.Account.Subscription.Status"]}</span>
          <div>{i18n[getStripeSubscriptionStatusI18nKey(subscription.status)]}</div>
        </div>
        <div className="billing-portal-access">
          <I18nAction action={() => billingPortalFormRef.current.submit()}>
            {i18n["Page.Settings.Account.Subscription.BillingPortal.Link"]}
          </I18nAction>
          <form
            ref={billingPortalFormRef}
            className="none"
            target="_blank"
            method="post"
            action={subscription.billingPortalUrl}
          >
            <input name="_csrf" type="hidden" value={csrf} />
          </form>
        </div>
      </div>
    </div>
  );
}

function ReactivationModal({
                             country,
                             setDisplayReactivationModal,
                             subscription,
                           }: {
  country?: Country;
  setDisplayReactivationModal: (boolean: boolean) => any;
  subscription: Subscription;
}) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [coupon, setCoupon] = useState<string>();
  const [reactivationOnGoing, setReactivationOnGoing] = useState<boolean>(false);

  function reactivationPromise() {
    return async () => {
      setReactivationOnGoing(true);
      try {
        await sfetch(subscription.reactivateUrl, {
          method: "PUT",
          body: JSON.stringify({
            payment: {
              coupon,
            },
          }),
          headers: {
            "Content-Type": "application/json",
          },
        }).then(() => setReactivationOnGoing(false));
        document.location.reload();
      } catch (e) {
        setErrorMessage(i18n["Global.Status.technical_error"]);
        Sentry.captureException(e);
        setReactivationOnGoing(false);
      }
    };
  }

  return (
    <ModalConfirm
      title={i18n["Page.Settings.Account.Subscription.Reactivate.Modal.Title"]}
      cancelButtonLabel={i18n["Global.Cancel"]}
      actionButtonLabel={i18n["Page.Settings.Account.Subscription.Reactivate.Modal.Action"]}
      cancel={() => setDisplayReactivationModal(false)}
      action={reactivationPromise()}
      cancelEnabled={!reactivationOnGoing}
    >
      {errorMessage && <div className="form-general-error message danger">{errorMessage}</div>}
      <SettingsReactivateAccountForm
        priceExcludingTaxInCents={subscription.reactivationPlan.priceExcludingTaxInCents}
        currency={subscription.reactivationPlan.currency}
        billedInterval={subscription.reactivationPlan.billedInterval}
        trialPeriodDay={subscription.reactivationPlan.trialPeriodDay}
        taxRate={subscription.reactivationPlan.taxRates[country?.name]}
        validateEndpoint={subscription.validateEndpoint}
        onErrorMessageChanged={setErrorMessage}
        onCouponChanged={setCoupon}
      />
    </ModalConfirm>
  );
}

function UnsubscribeModal({
                            setDisplayUnsubscribeModal,
                            unsubscribeUrl,
                          }: {
  setDisplayUnsubscribeModal: (boolean) => any;
  unsubscribeUrl: string;
}) {
  const [errorMessage, setErrorMessage] = useState<string>();
  return (
    <ModalDelete
      title={i18n["Page.Settings.Account.Subscription.Unsubscribe.Modal.Title"]}
      actionButtonLabel={i18n["Page.Settings.Account.Subscription.Unsubscribe.Action"]}
      cancel={() => setDisplayUnsubscribeModal(false)}
      action={async () => {
        try {
          await sfetch(unsubscribeUrl, {
            method: "DELETE",
          });
          document.location.reload();
        } catch (e) {
          setErrorMessage(i18n["Global.Status.technical_error"]);
          Sentry.captureException(e);
        }
      }}
    >
      {errorMessage && <div className="form-general-error message danger">{errorMessage}</div>}
      {i18n["Page.Settings.Account.Subscription.Unsubscribe.Modal.Content"]}
    </ModalDelete>
  );
}

SettingsAccount.loadComponent = (elementId: string, props) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<SettingsAccount {...props} />);
};
